/**
 * /no-contract-sim — SEO landing page
 * Target: "no contract SIM", "rolling SIM UK", "30-day SIM", "monthly SIM card UK"
 * Research: All competitors offer this but none have a DEDICATED landing page with FAQ schema.
 */
import { Link } from 'react-router-dom'
import { ArrowRight, Shield, Calendar, Zap, RefreshCw } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import SiluxPromise from '@/components/SiluxPromise'

const FAQS = [
  {
    question: 'What is a no-contract SIM card?',
    answer: 'A no-contract SIM card (also called a rolling or 30-day SIM) runs on a monthly rolling plan. You pay month-to-month with no minimum term, and you can cancel, switch or upgrade at any time.',
  },
  {
    question: 'Can I keep my number with a no-contract SIM?',
    answer: 'Not on a Pay&Go (no-contract) SIM — due to our agreements with the networks, we are unable to PAC transfer a number onto a Pay&Go SIM. To keep your number, choose a SIM contract: number transfers (PAC) are free and we process them Monday to Thursday. Three to Three takes 2–3 working days; from another network (for example O2 to Three, Vodafone to O2 or Vodafone to Three) it takes 1–2 working days.',
  },
  {
    question: 'Are no-contract SIMs more expensive?',
    answer: 'No — our no-contract SIM plans are priced from just £5/month. Rolling plans don\'t cost more; you just have the freedom to leave at any time without penalty.',
  },
  {
    question: 'How do I cancel my no-contract SIM?',
    answer: 'Simply give 30 days\' notice. To cancel, use the online Cancel Contract page in your Silux Mobile account (log in, then go to Cancel Contract) or email info@siluxmobile.co.uk.',
  },
  {
    question: 'Will my price increase during my contract?',
    answer: 'No. Silux Mobile never raises your price mid-contract — there are no price increases during your contract and no annual or inflation-linked price rises. The price you sign up at is the price you pay.',
  },
  {
    question: 'Are plan and SIM upgrades free?',
    answer: 'Yes. Upgrades are free — move to a bigger data plan or a better SIM whenever you like at no extra cost, with no upgrade or admin fees.',
  },
  {
    question: 'Do you run a credit check?',
    answer: 'No. There are no credit checks on our SIM contracts, so you can join Silux Mobile regardless of your credit history. No credit check is required to order or activate your SIM.',
  },
  {
    question: 'Is Silux Mobile a good mobile provider?',
    answer: 'Yes. Silux Mobile is a UK SIM-only and mobile network provider with SIMs on Three, Vodafone, EE and O2, with cheap SIM deals, no price rises, free upgrades and no credit checks on SIM contracts.',
  },
]

export default function NoContractSim() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'No Contract SIM', url: 'https://www.siluxmobile.co.uk/no-contract-sim' },
  ]

  return (
    <>
      <SEOHead
        title="No Contract SIM Cards UK | 30-Day Rolling SIM Plans | Silux Mobile"
        description="No-contract SIM cards from Silux Mobile. 30-day rolling plans — no price increases. Data, calls & text SIMs from £5/month on Three 5G. UK delivery. Free upgrades and no credit checks on SIM contracts."
        keywords={[
          'no contract SIM', 'no contract SIM card UK', '30 day SIM', 'rolling SIM card',
          'monthly SIM card UK', 'pay monthly SIM no contract', 'cancel anytime SIM',
          'flexible SIM UK', 'no commitment SIM card', 'short term SIM UK',
          'SIM only provider', 'best SIM only provider UK', 'mobile network provider',
          'cheap mobile provider', 'pay monthly SIM provider',
        ]}
        canonical="https://www.siluxmobile.co.uk/no-contract-sim"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />

      <section className="py-20 px-4 bg-gradient-to-br from-green-700 to-teal-600 text-white text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <Shield className="w-4 h-4" />
            No Lock-In — Cancel Anytime
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            No Contract SIM Cards UK
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            All Silux Mobile SIM plans are from Pay&amp;Go rolling 30-day SIMs, and contracts with no price increases.
            Switch or cancel anytime with a SIM only provider and mobile network provider you can trust. Powered by Three's 5G network.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/sim-only" className="px-8 py-4 bg-white text-green-700 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all">
              Browse Plans
            </Link>
            <Link to="/sim-only" className="px-8 py-4 bg-white/20 border-2 border-white/40 text-white rounded-2xl font-bold hover:bg-white/30 transition-all">
              Free SIM Card
            </Link>
          </div>
        </div>
      </section>

      <SiluxPromise variant="sim" />

      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Why Choose No-Contract?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Calendar, title: '30-Day Rolling', desc: 'Pay month-to-month. No 12 or 24-month tie-in.' },
              { icon: RefreshCw, title: 'Switch Anytime', desc: 'Upgrade, downgrade or switch plan at any time.' },
              { icon: Shield, title: 'No Price Increases', desc: 'Your price is locked — no price rises.' },
              { icon: Zap, title: '5G Included', desc: 'Full 5G access on Three\'s network, no extra cost.' },
            ].map(item => (
              <div key={item.title} className="flex gap-4 p-6 bg-gray-50 rounded-2xl">
                <item.icon className="w-8 h-8 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">No-Contract SIM FAQs</h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <details key={faq.question} className="group border border-gray-200 rounded-xl overflow-hidden bg-white">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900">
                  {faq.question}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Related Pages</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Cheap SIM Cards', to: '/cheap-sim-cards' },
              { label: 'Data SIM Cards', to: '/data-sim-cards' },
              { label: 'Free SIM Card', to: '/sim-only' },
              { label: 'SIM Only Deals', to: '/sim-only' },
              { label: 'Mobile Broadband', to: '/mobile-broadband' },
              { label: 'Network Coverage', to: '/network-coverage' },
            ].map(link => (
              <Link key={link.to} to={link.to} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-green-50 hover:text-green-700 transition-all">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
